import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, CheckCircle, XCircle, Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { HttpError, handleApiError } from "@/lib/errors";
import { Seo } from "@/components/Seo";
import { trackFastgiverCompleted } from "@/lib/analytics";

const FastgiverBekreftelse = () => {
  const [status, setStatus] = useState<"loading" | "success" | "failed">("loading");
  const [trackedId, setTrackedId] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check agreement status from URL params if available
    const params = new URLSearchParams(window.location.search);
    const agreementId = params.get("agreementId");

    if (!agreementId) {
      // No agreement ID means user navigated here directly or Vipps didn't pass it
      setStatus("success");
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await fetch(
          `/api/vipps-recurring?action=get-agreement&id=${agreementId}`
        );
        if (!res.ok) throw new HttpError(res.status, "Kunne ikke hente status");
        const data = await res.json();

        if (data.status === "ACTIVE") {
          setStatus("success");
          // Track completion én gang per agreement (idempotent for retries
          // av samme polling-loop).
          if (trackedId !== agreementId) {
            setTrackedId(agreementId);
            trackFastgiverCompleted({
              agreementId,
              amountNok: typeof data.amount === "number" ? data.amount : 0,
            });
          }
        } else if (data.status === "STOPPED" || data.status === "EXPIRED") {
          setStatus("failed");
        } else {
          setTimeout(checkStatus, 3000);
        }
      } catch (error) {
        handleApiError(error, {
          source: "FastgiverBekreftelse.checkStatus",
          silent: true,
          context: { agreementId },
        });
        // Vipps redirected the user back, so the agreement was almost certainly
        // created — optimistically show success rather than scaring the user.
        setStatus("success");
      }
    };

    checkStatus();
  }, []);

  return (
    <Layout>
      <Seo
        title="Bekreftelse – Håp Naturlab"
        description="Takk for at du støtter Håp Naturlab!"
        noIndex
      />
      <section className="mx-auto max-w-2xl px-6 py-20 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-primary animate-spin" />
            <h1 className="mt-6 text-3xl font-bold font-heading">
              Venter på bekreftelse...
            </h1>
            <p className="mt-4 text-muted-foreground font-body">
              Vi sjekker statusen på avtalen din med Vipps.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="mt-6 text-3xl font-bold font-heading">
              Tusen takk!
            </h1>
            <p className="mt-4 text-lg text-muted-foreground font-body">
              Din faste giveravtale er opprettet. Du gjør en utrolig forskjell
              for syke barn.
            </p>
            <div className="mt-8 flex items-center justify-center gap-2 text-primary">
              <Heart className="h-5 w-5" />
              <span className="font-body font-semibold">
                Sammen skaper vi håp
              </span>
            </div>

            <div className="mt-10 rounded-2xl border border-border bg-secondary/30 p-6 text-left">
              <h2 className="font-heading text-lg font-bold">
                Slik styrer du avtalen i Vipps-appen
              </h2>
              <ol className="mt-3 space-y-2 text-sm text-muted-foreground font-body">
                <li>1. Åpne Vipps-appen og trykk på <strong>Profil</strong>.</li>
                <li>2. Velg <strong>Faste betalinger</strong> i menyen.</li>
                <li>3. Finn <strong>Håp Naturlab</strong> og trykk deg inn.</li>
                <li>4. Her kan du når som helst endre beløp eller <strong>Avslutte</strong> avtalen.</li>
              </ol>
              <p className="mt-4 text-xs text-muted-foreground font-body">
                Ingen bindingstid. Oppga du e-post sender vi deg også månedlig kvittering for skattefradrag.
              </p>
            </div>

            <Link
              to="/"
              className="mt-8 inline-flex rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground font-body shadow-lg transition-all hover:opacity-90"
            >
              Tilbake til forsiden
            </Link>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="mt-6 text-3xl font-bold font-heading">
              Noe gikk galt
            </h1>
            <p className="mt-4 text-lg text-muted-foreground font-body">
              Avtalen ble ikke fullført. Du kan prøve igjen eller kontakte oss
              hvis du trenger hjelp.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex rounded-full border-2 border-primary px-8 py-3.5 text-sm font-semibold text-primary font-body transition-all hover:bg-primary hover:text-primary-foreground"
              >
                Kontakt oss
              </Link>
              <Link
                to="/"
                className="inline-flex rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground font-body shadow-lg transition-all hover:opacity-90"
              >
                Tilbake til forsiden
              </Link>
            </div>
          </>
        )}
      </section>
    </Layout>
  );
};

export default FastgiverBekreftelse;
